import { Suspense, useMemo, useCallback, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import { ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import type { MoldModelProps } from "../types";
import type { CasterTreeRow } from "../../../models";
import {
  GLB_NODE_TO_POSITION,
  ASSEMBLY_TO_POSITION,
  FOOT_ROLL_CHILD_TO_SUB,
  HOVER_EMISSIVE_INTENSITY,
  SELECTION_EMISSIVE_INTENSITY,
  EMPTY_POSITION_COLOR,
  MODEL_POSITION,
  MODEL_SCALE,
} from "../constants";
import type { NodeMapping } from "../constants";
import {
  Viewer3DMaterials,
  Viewer3DEmissiveColors,
  Viewer3DGeometry,
  Viewer3DSettings,
  getComponentColor,
} from "../../../theme/colors";

// Gray color for empty positions (no part installed)
const EMPTY_COLOR = new THREE.Color(EMPTY_POSITION_COLOR);

// Path to the GLB model
const MOLD_MODEL_PATH = new URL("../assets/MoldMESModel.glb", import.meta.url)
  .href;

interface MoldModelInnerProps {
  moldData: MoldModelProps["moldData"];
  selectedLocationId: string | null;
  hoveredLocationId: string | null;
  onSelectLocation: (row: CasterTreeRow | null) => void;
  onHoverLocation: (locationId: string | null) => void;
}

const MoldModelInner = ({
  moldData,
  selectedLocationId,
  hoveredLocationId,
  onSelectLocation,
  onHoverLocation,
}: MoldModelInnerProps) => {
  const { scene } = useGLTF(MOLD_MODEL_PATH);
  const groupRef = useRef<THREE.Group>(null);

  // Get locationId from NodeMapping
  const getLocationId = useCallback(
    (mapping: NodeMapping): string | null => {
      if (mapping.subPositionId !== undefined) {
        // Sub-component: location-{moldId}-{positionId}-{subPositionId}
        return `location-${moldData.moldId}-${mapping.positionId}-${mapping.subPositionId}`;
      }
      // Standalone component: location-{moldId}-{positionId}
      return `location-${moldData.moldId}-${mapping.positionId}`;
    },
    [moldData.moldId],
  );

  // Find location data by NodeMapping
  const getLocationByMapping = useCallback(
    (mapping: NodeMapping): CasterTreeRow | null => {
      const targetLocationId = getLocationId(mapping);
      if (targetLocationId === null) return null;
      return (
        moldData.locations.find((loc) => loc.id === targetLocationId) ?? null
      );
    },
    [moldData.locations, getLocationId],
  );

  // Find NodeMapping from mesh by traversing UP to the parent GROUP node.
  // Unlike Sidewall (unique mesh names), Mold meshes are all named "Solid1".
  // The identity comes from the GROUP parent node name in the GLB hierarchy.
  //
  // Foot Roll Assemblies need special handling: the GLB reuses identical child
  // names (all ":1") across all 4 assemblies. The traversal must walk past
  // the child to find the Assembly parent and combine both to get the mapping.
  const getMappingFromObject = useCallback(
    (object: THREE.Object3D): NodeMapping | null => {
      let current: THREE.Object3D | null = object;
      let footRollSubPositionId: number | null = null;

      while (current) {
        if (current.name) {
          // Check for Foot Roll Assembly parent (resolves ambiguous children)
          if (ASSEMBLY_TO_POSITION[current.name] !== undefined) {
            const positionId = ASSEMBLY_TO_POSITION[current.name];
            return footRollSubPositionId !== null
              ? { positionId, subPositionId: footRollSubPositionId }
              : { positionId };
          }

          // Check for Foot Roll child (ambiguous — remember sub and keep walking)
          if (FOOT_ROLL_CHILD_TO_SUB[current.name] !== undefined) {
            footRollSubPositionId = FOOT_ROLL_CHILD_TO_SUB[current.name];
            current = current.parent;
            continue;
          }

          // Check direct mapping (standalone components, flanges, collars, seals, sprays)
          if (GLB_NODE_TO_POSITION[current.name] !== undefined) {
            return GLB_NODE_TO_POSITION[current.name];
          }
        }
        current = current.parent;
      }
      return null;
    },
    [],
  );

  // Create a deep clone of the scene with interactive materials
  const clonedScene = useMemo(() => {
    const clone = scene.clone(true);

    // Deep clone materials and store original colors
    clone.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (Array.isArray(child.material)) {
          child.material = child.material.map((m: THREE.Material) => {
            const cloned = m.clone();
            if (cloned instanceof THREE.MeshStandardMaterial) {
              cloned.userData.originalColor = cloned.color.clone();
            }
            return cloned;
          });
        } else if (child.material) {
          const cloned = child.material.clone();
          if (cloned instanceof THREE.MeshStandardMaterial) {
            cloned.userData.originalColor = cloned.color.clone();
          }
          child.material = cloned;
        }
      }
    });

    return clone;
  }, [scene]);

  // Check if a location has an installed part
  const hasInstalledPart = useCallback(
    (locationId: string): boolean => {
      const location = moldData.locations.find((loc) => loc.id === locationId);
      return location?.HasAssignedPart ?? false;
    },
    [moldData.locations],
  );

  // Update materials based on hover/selection state and installation status
  useMemo(() => {
    if (!clonedScene) return;

    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        // Find which location this mesh belongs to
        const mapping = getMappingFromObject(child);
        const meshLocationId = mapping ? getLocationId(mapping) : null;

        // Determine selection/hover/installed state
        const isSelected =
          meshLocationId !== null && meshLocationId === selectedLocationId;
        const isHovered =
          meshLocationId !== null && meshLocationId === hoveredLocationId;
        const isInstalled =
          meshLocationId !== null && hasInstalledPart(meshLocationId);

        // Apply color and emissive effect based on state
        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];

        materials.forEach((mat) => {
          if (mat instanceof THREE.MeshStandardMaterial) {
            // Apply color based on installation status
            if (isInstalled && mapping) {
              // Use theme color based on position (supports sub-positions)
              const themeColor = getComponentColor(
                mapping.positionId,
                mapping.subPositionId,
              );
              mat.color.set(themeColor);
              mat.metalness = Viewer3DMaterials.STANDARD_METALNESS;
              mat.roughness = Viewer3DMaterials.STANDARD_ROUGHNESS;
            } else {
              // Use gray color for empty positions
              mat.color.copy(EMPTY_COLOR);
              mat.metalness = Viewer3DMaterials.EMPTY_METALNESS;
              mat.roughness = Viewer3DMaterials.EMPTY_ROUGHNESS;
            }

            // Apply emissive effect based on selection/hover state
            if (isSelected) {
              mat.emissive = new THREE.Color(Viewer3DEmissiveColors.HIGHLIGHT);
              mat.emissiveIntensity = SELECTION_EMISSIVE_INTENSITY;
            } else if (isHovered) {
              mat.emissive = new THREE.Color(Viewer3DEmissiveColors.HIGHLIGHT);
              mat.emissiveIntensity = HOVER_EMISSIVE_INTENSITY;
            } else {
              mat.emissive = new THREE.Color(Viewer3DEmissiveColors.DEFAULT);
              mat.emissiveIntensity =
                Viewer3DSettings.DEFAULT_EMISSIVE_INTENSITY;
            }
          }
        });
      }
    });
  }, [
    clonedScene,
    selectedLocationId,
    hoveredLocationId,
    getMappingFromObject,
    getLocationId,
    hasInstalledPart,
  ]);

  // Handle click on the model
  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      event.stopPropagation();

      const mapping = getMappingFromObject(event.object);
      if (mapping !== null) {
        const locationId = getLocationId(mapping);
        if (locationId !== null) {
          const location = getLocationByMapping(mapping);
          onSelectLocation(location);
        }
      }
    },
    [
      getMappingFromObject,
      getLocationId,
      getLocationByMapping,
      onSelectLocation,
    ],
  );

  // Handle pointer over (hover)
  const handlePointerOver = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();

      const mapping = getMappingFromObject(event.object);

      if (mapping !== null) {
        const locationId = getLocationId(mapping);
        if (locationId !== null) {
          document.body.style.cursor = "pointer";
          onHoverLocation(locationId);
        }
      }
    },
    [getMappingFromObject, getLocationId, onHoverLocation],
  );

  // Handle pointer out (hover end)
  const handlePointerOut = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      document.body.style.cursor = "auto";
      onHoverLocation(null);
    },
    [onHoverLocation],
  );

  return (
    <group ref={groupRef} position={MODEL_POSITION}>
      <primitive
        object={clonedScene}
        scale={MODEL_SCALE}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      />
    </group>
  );
};

export const MoldModel = ({
  moldData,
  selectedLocationId,
  hoveredLocationId,
  onSelectLocation,
  onHoverLocation,
}: MoldModelProps) => {
  return (
    <Suspense
      fallback={
        <mesh>
          <boxGeometry
            args={[
              Viewer3DGeometry.FALLBACK_BOX_SIZE,
              Viewer3DGeometry.FALLBACK_BOX_SIZE,
              Viewer3DGeometry.FALLBACK_BOX_SIZE,
            ]}
          />
          <meshStandardMaterial color="red" />
        </mesh>
      }
    >
      <MoldModelInner
        moldData={moldData}
        selectedLocationId={selectedLocationId}
        hoveredLocationId={hoveredLocationId}
        onSelectLocation={onSelectLocation}
        onHoverLocation={onHoverLocation}
      />
    </Suspense>
  );
};

// Preload the GLB model
useGLTF.preload(MOLD_MODEL_PATH);
